/**
 * ApplicationWindowsPage — `/pulse/windows`.
 *
 * Detail view for the Spray/Feed/Water windows tile on the main dashboard.
 * Pulls the open/closed status from `/api/dashboard/now`'s `windows` block
 * and lays out the "why each is closed" narrative the user keeps asking for.
 */
import React, { useEffect, useState } from 'react';
import { Ban, AlertTriangle, Brain, Droplets, Leaf, Wind } from 'lucide-react';
import { PulseDetailLayout, PulseInsight } from '../../components/pulse/PulseDetailLayout';
import { API_URL } from '../../config/api';

const ICON_FOR = {
  spray: Wind, feed: Leaf, water: Droplets,
};

function Row({ kind, status }) {
  const Icon = ICON_FOR[kind] || Ban;
  const open = status === 'OPEN';
  return (
    <div data-testid={`window-row-${kind}`}
         className="flex items-center justify-between bg-slate-900/80 border border-slate-800 rounded-2xl px-4 py-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          open ? 'bg-emerald-500/15' : 'bg-rose-500/10'
        }`}>
          <Icon className={`w-5 h-5 ${open ? 'text-emerald-400' : 'text-rose-400'}`} />
        </div>
        <div>
          <div className="text-base font-semibold capitalize text-slate-100">{kind}</div>
          <div className="text-[11px] text-slate-400">
            {open ? 'Open — input will be effective' : 'Closed — input will be wasted'}
          </div>
        </div>
      </div>
      <span className={`text-xs font-bold tracking-wider px-2 py-1 rounded-full ${
        open ? 'text-emerald-300 bg-emerald-500/10' : 'text-rose-300 bg-rose-500/10'
      }`}>
        {open ? 'OPEN' : 'CLOSED'}
      </span>
    </div>
  );
}

export default function ApplicationWindowsPage() {
  const [now, setNow] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/dashboard/now`, { credentials: 'include' })
      .then(r => r.json()).then(setNow).catch(() => {});
  }, []);

  const w = now?.windows || {};
  const m = now?.metrics || {};

  // "Why everything is closed" — surface the most-likely closure reason
  // for each window so the user gets a *receipt*, not just a status.
  const reasons = [];
  const vpd = m.vpd?.value;
  const ec  = m.ec?.value;
  const moisture = m.moisture?.value;
  if (vpd != null && vpd < 0.4) reasons.push('Humidity too high (VPD < 0.4 — no transpiration)');
  if (moisture != null && moisture > 60) reasons.push('Soil already full (moisture > 60%)');
  if (ec != null && ec > 900) reasons.push('EC elevated — nutrients still processing');

  return (
    <PulseDetailLayout
      title="Application Windows"
      icon={Ban}
      iconColor="text-rose-400"
      bigValue={null}
      bigValueColor="text-rose-400"
    >
      <div className="space-y-3">
        <Row kind="spray" status={w.spray?.status} />
        <Row kind="feed"  status={w.feed?.status} />
        <Row kind="water" status={w.water?.status} />
      </div>

      <PulseInsight tone="amber" icon={Brain} label="Why" testid="windows-why">
        {reasons.length === 0
          ? 'Conditions are mixed. See each window for specifics.'
          : (
            <ul className="space-y-1 mt-1">
              {reasons.map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
          )}
      </PulseInsight>

      <PulseInsight tone="rose" icon={AlertTriangle} label="Insight" testid="windows-insight">
        Acting now wastes product. TurfPulse will alert you the moment a window opens.
      </PulseInsight>
    </PulseDetailLayout>
  );
}
